import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ConfirmModalComponent } from '../../../shared/components/ui/confirm-modal/confirm-modal.component';
import { ImageLightboxComponent } from '../../../shared/components/ui/image-lightbox/image-lightbox.component';
import { PrettySelectOption } from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { VehiclePhotosService } from '../../../shared/services/vehicle-photos.service';
import { VehiclesService } from '../../../shared/services/vehicles.service';
import { Image, ImageType } from '../../../shared/types/vehicle.types';
import { TPipe } from '../../../shared/pipe/t.pipe';

// Valeurs = **clés i18n** : résolues au rendu (`| t`) et par
// `<app-pretty-select>`, qui traduit libellé, déclencheur et filtre.
const IMAGE_TYPE_LABEL: Record<ImageType, string> = {
  GENERAL: 'label.imageType.GENERAL',
  REGISTRATION_CARD: 'label.imageType.REGISTRATION_CARD',
  INSURANCE: 'label.imageType.INSURANCE',
  TECHNICAL_INSPECTION: 'label.imageType.TECHNICAL_INSPECTION',
};

const IMAGE_TYPE_OPTIONS: PrettySelectOption<ImageType>[] = (
  Object.entries(IMAGE_TYPE_LABEL) as [ImageType, string][]
).map(([value, label]) => ({ value, label }));

/**
 * Galerie photos d'un véhicule owner — upload, changement de catégorie,
 * suppression et définition de l'image de couverture.
 *
 * Liste filtrée par {@link ImageType} via un tab switch (Général /
 * Carte grise / Assurance / Visite technique). L'upload multipart cible la
 * catégorie active. Le bouton "Définir comme couverture" n'est visible que
 * pour les photos `GENERAL` (les seules visibles au public catalogue).
 *
 * Émet {@link coverChanged} quand l'imgCover du véhicule change (le parent
 * doit refresh le signal véhicule).
 */
@Component({
  selector: 'app-vehicle-photos',
  standalone: true,
  imports: [CommonModule, ConfirmModalComponent, ImageLightboxComponent, TPipe],
  templateUrl: './vehicle-photos.component.html',
  styleUrls: ['./vehicle-photos.component.scss'],
})
export class VehiclePhotosComponent implements OnInit {
  @Input({ required: true }) vehicleId!: number;
  /** URL de l'image de couverture courante. Sert à matcher la photo active. */
  @Input() currentCoverUrl: string | null = null;

  /** Émis quand l'imgCover change côté back — le parent doit refetch le vehicle. */
  @Output() readonly coverChanged = new EventEmitter<string>();

  private readonly photosService = inject(VehiclePhotosService);
  private readonly vehiclesService = inject(VehiclesService);
  private readonly destroyRef = inject(DestroyRef);

  readonly typeLabel = IMAGE_TYPE_LABEL;
  readonly typeOptions = IMAGE_TYPE_OPTIONS;

  readonly activeType = signal<ImageType>('GENERAL');
  readonly photos = signal<Image[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);

  /** URL affichée en grand dans la lightbox (null = fermée). */
  readonly lightboxSrc = signal<string | null>(null);

  readonly uploading = signal(false);
  readonly uploadError = signal<string | null>(null);

  /** Spinners par photo pour les opérations granulaires (delete / setCover / changeType). */
  readonly busyPhotoId = signal<number | null>(null);

  // Delete confirm modal
  readonly deleteOpen = signal(false);
  readonly deleting = signal(false);
  readonly deleteError = signal<string | null>(null);
  readonly photoPendingDelete = signal<Image | null>(null);

  ngOnInit(): void {
    this.load();
  }

  switchType(t: ImageType): void {
    if (t === this.activeType()) return;
    this.activeType.set(t);
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.photosService
      .listMine(this.vehicleId, this.activeType())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (list) => {
          this.photos.set(list ?? []);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.photos.set([]);
          this.loading.set(false);
          this.error.set(toErrorMessage(err, 'errors.photosLoadFailed'));
        },
      });
  }

  // ── Upload ──────────────────────────────────────────────────────────────
  onFilesSelected(input: HTMLInputElement): void {
    const files = Array.from(input.files ?? []);
    if (!files.length) return;
    this.uploading.set(true);
    this.uploadError.set(null);
    this.photosService
      .uploadMine(this.vehicleId, files, this.activeType())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.uploading.set(false);
          input.value = '';
          this.load();
        },
        error: (err: unknown) => {
          this.uploading.set(false);
          input.value = '';
          this.uploadError.set(toErrorMessage(err, "L'upload a échoué."));
        },
      });
  }

  // ── Set cover (uniquement GENERAL) ──────────────────────────────────────
  setAsCover(photo: Image): void {
    if (photo.id == null || photo.type !== 'GENERAL') return;
    this.busyPhotoId.set(photo.id);
    this.vehiclesService
      .setCoverImage(this.vehicleId, photo.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (v) => {
          this.busyPhotoId.set(null);
          this.coverChanged.emit(v.imgCover ?? '');
        },
        error: (err: unknown) => {
          this.busyPhotoId.set(null);
          this.error.set(toErrorMessage(err, "Impossible de définir comme couverture."));
        },
      });
  }

  // ── Change type ────────────────────────────────────────────────────────
  changePhotoType(photo: Image, newType: ImageType): void {
    if (photo.id == null || newType === photo.type) return;
    this.busyPhotoId.set(photo.id);
    this.photosService
      .updateTypeMine(this.vehicleId, photo.id, newType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.busyPhotoId.set(null);
          // La photo change de catégorie → elle sort de l'onglet actif.
          this.load();
        },
        error: (err: unknown) => {
          this.busyPhotoId.set(null);
          this.error.set(toErrorMessage(err, 'errors.categoryChangeFailed'));
        },
      });
  }

  // ── Delete ──────────────────────────────────────────────────────────────
  openDelete(photo: Image): void {
    this.photoPendingDelete.set(photo);
    this.deleteError.set(null);
    this.deleteOpen.set(true);
  }
  closeDelete(): void {
    if (this.deleting()) return;
    this.deleteOpen.set(false);
    this.photoPendingDelete.set(null);
  }
  confirmDelete(): void {
    const photo = this.photoPendingDelete();
    if (!photo?.id) return;
    this.deleting.set(true);
    this.deleteError.set(null);
    this.photosService
      .deleteMine(this.vehicleId, photo.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.deleting.set(false);
          this.deleteOpen.set(false);
          this.photoPendingDelete.set(null);
          this.load();
        },
        error: (err: unknown) => {
          this.deleting.set(false);
          this.deleteError.set(toErrorMessage(err, 'errors.deleteFailed'));
        },
      });
  }

  isCurrentCover(photo: Image): boolean {
    return !!this.currentCoverUrl && photo.url === this.currentCoverUrl;
  }

  /** `accept` du file input : images seules pour le catalogue (GENERAL),
   *  images + PDF pour les onglets documents (carte grise, assurance…). */
  get acceptAttr(): string {
    return this.activeType() === 'GENERAL' ? 'image/*' : 'image/*,application/pdf';
  }

  /** Une entrée est un document (PDF/Office) — affichée en placeholder
   *  ouvrable, pas en `<img>`. Détection sur l'extension de l'URL. */
  isDocument(url: string | null | undefined): boolean {
    if (!url) return false;
    const clean = url.split('?')[0].toLowerCase();
    return /\.(pdf|docx?|xlsx?|pptx?)$/.test(clean);
  }

  /** Ouvre une image en grand dans la lightbox. */
  openLightbox(url: string | null | undefined): void {
    if (url) this.lightboxSrc.set(url);
  }

  /** Ouvre un document (PDF…) dans un nouvel onglet. */
  openDoc(url: string | null | undefined): void {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }
}
